<app-modal
  [isVisible]="isVisible()"
  [config]="modalConfig"
  [warnBeforeClose]="hasUnsavedWork() && !isCreating()"
  (cancelled)="onDismiss()"
  (closed)="onDismiss()"
  (confirmed)="onContinue()"
  (secondary)="onBack()"
>
  @if (isCreating()) {
    <app-modal-status
      variant="loading"
      [title]="'modals.doc_creation.creating_status' | transloco"
      [description]="'modals.doc_creation.creating_description' | transloco"
    >
      <app-huxley [state]="'thinking'" [size]="72" />
    </app-modal-status>
  } @else if (successMessage(); as message) {
    <app-modal-status
      variant="success"
      [title]="message"
      [description]="'modals.doc_creation.success_description' | transloco"
      [linkHref]="successOpenUrl()"
      [linkLabel]="'modals.doc_creation.success_open_link' | transloco"
    >
      <app-huxley [state]="'success'" [size]="72" />
    </app-modal-status>
  } @else {
    <!-- Step Indicator -->
    <app-step-indicator
      [steps]="stepConfigs()"
      [currentStep]="currentStep()"
      [maxReachedStep]="maxReachedStep()"
      [clickable]="true"
      (stepClicked)="onStepClicked($event)"
    ></app-step-indicator>

    @if (currentStep() === 'document-type') {
      <div class="step-body">
        <!-- Document name field — always visible -->
        <div class="field">
          <label for="documentName">
            {{ 'modals.doc_creation.field_document_name' | transloco }}
            <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <input
            id="documentName"
            type="text"
            [placeholder]="'modals.doc_creation.name_placeholder' | transloco"
            [value]="documentName()"
            (input)="onDocumentNameChange($any($event.target).value)"
          />
        </div>

        <!-- Language lives on a dedicated step immediately after this
           one — extracted so the doc-type step can focus on picking
           the category + subtype. -->

        @if (_docTypeSubStep() === 'category') {
          <!-- Sub-step 1: pick a category. Custom stays on this sub-step and
             renders the custom-name input inline below the grid — Custom is a
             single-subtype catch-all, so the picker has nothing to add. -->
          <div class="dtp-list">
            @for (cat of documentTypes(); track cat.id) {
              <div
                class="dtp-row"
                [class.selected]="_selectedCategory()?.id === cat.id"
                (click)="onCategorySelect(cat)"
                (keydown.enter)="onCategorySelect(cat)"
                (keydown.space)="onCategorySelect(cat)"
                tabindex="0"
                role="button"
                [attr.aria-pressed]="_selectedCategory()?.id === cat.id"
              >
                @let catKind = getCategoryKindFromCode(cat.code);
                @if (catKind) {
                  <app-category-icon [category]="catKind" />
                }
                <div class="dtp-info">
                  <div class="dtp-name">{{ cat | localizedName: locale() }}</div>
                </div>
                @if (cat.code === 'custom' && _selectedCategory()?.id === cat.id) {
                  <mat-icon class="dtp-check" aria-hidden="true">check</mat-icon>
                } @else {
                  <mat-icon class="dtp-chevron" aria-hidden="true">chevron_right</mat-icon>
                }
              </div>
            }
            @if (documentTypes().length === 0) {
              <p class="dtp-loading">{{ 'common.loading' | transloco }}</p>
            }
          </div>

          @if (isCustomCategory()) {
            <div class="field custom-name-field">
              <label for="customTypeName">
                {{ 'modals.doc_creation.custom_name_label' | transloco }}
                <span class="required-mark" aria-hidden="true">*</span>
              </label>
              <input
                id="customTypeName"
                type="text"
                [placeholder]="'modals.doc_creation.custom_name_placeholder' | transloco"
                [value]="_customTypeName()"
                (input)="onCustomTypeNameChange($any($event.target).value)"
              />
            </div>
          }
        } @else {
          <!-- Sub-step 2: pick a subtype. Only reached for non-Custom
             categories — Custom is handled inline above. -->
          <div class="dtp-list">
            @for (sub of _selectedCategory()!.subtypes; track sub.id) {
              <div
                class="dtp-row"
                [class.selected]="_selectedSubtype()?.id === sub.id"
                (click)="onSubtypeSelect(sub)"
                (keydown.enter)="onSubtypeSelect(sub)"
                (keydown.space)="onSubtypeSelect(sub)"
                tabindex="0"
                role="button"
                [attr.aria-pressed]="_selectedSubtype()?.id === sub.id"
              >
                <app-kind-icon [kind]="getDocKindFromSubtypeCode(sub.code)" />
                <div class="dtp-info">
                  <div class="dtp-name">
                    {{ sub | localizedName: locale() }}
                  </div>
                  @if (sub | localizedDescription: locale(); as description) {
                    <div class="dtp-description">{{ description }}</div>
                  }
                </div>
                @if (_selectedSubtype()?.id === sub.id) {
                  <mat-icon class="dtp-check" aria-hidden="true">check</mat-icon>
                }
              </div>
            }
          </div>
        }
      </div>
    } @else if (currentStep() === 'language') {
      <!-- Step 2: Language.
         Extracted out of the doc-type step, where it competed for
         real-estate with the category/subtype picker. The validate
         call fires on this step's Continue so the fetched validation
         payload reflects the language locked in here. -->
      <div class="step-body">
        <p class="step-hint">{{ 'language_step.hint_document' | transloco }}</p>
        <app-language-picker [(language)]="contentLanguage" appearance="cards" />
      </div>
    } @else if (currentStep() === 'validation') {
      <!-- Step 3: Validation Results -->
      <div class="step-body">
        @if (validationLoading()) {
          <app-skeleton type="details" [count]="3"></app-skeleton>
        } @else if (validationError()) {
          <div class="validation-error" role="alert">
            <p class="error-message">{{ validationError() }}</p>
            <app-button
              type="primary"
              variant="outline"
              size="small"
              (clicked)="onRetryValidation()"
            >
              {{ 'common.retry' | transloco }}
            </app-button>
          </div>
        } @else if (validationResult()) {
          <div class="validation-results">
            <!-- Context Documents Section - Always shown at top -->
            <div class="validation-section context-documents-section">
              <header class="cdp-header">
                <div class="cdp-header-title-row">
                  <h4 class="cdp-title">
                    {{ 'modals.doc_creation.section_context_documents' | transloco }}
                  </h4>
                  <app-info-tip
                    [text]="contextDocsHelpText()"
                    position="below"
                    [ariaLabel]="'modals.doc_creation.about_context_docs_aria' | transloco"
                  />
                  @if (selectedContextDocuments().size > 0) {
                    <span class="cdp-count">{{
                      'modals.doc_creation.selected_count_suffix'
                        | transloco: { count: selectedContextDocuments().size }
                    }}</span>
                  }
                </div>
                <app-button
                  type="primary"
                  variant="outline"
                  size="small"
                  [disabled]="!resolvedOrg()"
                  (clicked)="onOpenUploadModal()"
                >
                  <mat-icon>upload_file</mat-icon>
                  {{ 'modals.doc_creation.upload_button' | transloco }}
                </app-button>
              </header>

              <!-- Context Documents Picker — shared `.panel-picker-*` chrome
                 (create-assessment, import-assistant, decisions-tab). -->
              @if (getContextDocuments().length > 0) {
                <div class="panel-picker-card">
                  <div class="panel-picker-search">
                    <mat-icon>search</mat-icon>
                    <input
                      type="text"
                      [placeholder]="'modals.doc_creation.search_documents_placeholder' | transloco"
                      [attr.aria-label]="'modals.doc_creation.search_documents_aria' | transloco"
                      [ngModel]="contextDocSearch()"
                      (ngModelChange)="onContextDocSearchValue($event)"
                    />
                  </div>

                  <div class="panel-picker-select-all">
                    <button
                      type="button"
                      class="panel-picker-select-all-btn"
                      [class.selected]="contextDocsSelectionState() === 'all'"
                      [class.indeterminate]="contextDocsSelectionState() === 'some'"
                      [disabled]="filteredContextDocsForModal().length === 0"
                      role="checkbox"
                      [attr.aria-checked]="
                        contextDocsSelectionState() === 'all'
                          ? 'true'
                          : contextDocsSelectionState() === 'some'
                            ? 'mixed'
                            : 'false'
                      "
                      (click)="toggleAllContextDocs()"
                    >
                      <span class="panel-picker-check">
                        <mat-icon>{{
                          contextDocsSelectionState() === 'some' ? 'remove' : 'check'
                        }}</mat-icon>
                      </span>
                      <span class="panel-picker-select-all-label">
                        {{ 'modals.doc_creation.select_all' | transloco }}
                      </span>
                    </button>
                    <span class="panel-picker-selection-count">
                      {{
                        'modals.doc_creation.selected_of_total'
                          | transloco
                            : {
                                selected: selectedContextDocuments().size,
                                total: filteredContextDocsForModal().length,
                              }
                      }}
                    </span>
                  </div>

                  @if (filteredContextDocsForModal().length === 0) {
                    <p class="panel-picker-empty">
                      {{ 'modals.doc_creation.no_docs_match_search' | transloco }}
                    </p>
                  } @else {
                    <div class="panel-picker-list panel-picker-list--modal">
                      @for (doc of filteredContextDocsForModal(); track doc.context_document_id) {
                        <div class="panel-picker-row">
                          <button
                            type="button"
                            class="panel-picker-item"
                            [class.selected]="isContextDocumentSelected(doc.context_document_id)"
                            [attr.aria-pressed]="isContextDocumentSelected(doc.context_document_id)"
                            (click)="onContextDocumentToggle(doc.context_document_id)"
                          >
                            <span class="panel-picker-check">
                              <mat-icon>check</mat-icon>
                            </span>
                            <app-type-icon
                              [icon]="getContextDocIconConfig(doc.file_type).icon"
                              [variant]="getContextDocIconConfig(doc.file_type).variant"
                              size="small"
                            />
                            <span class="panel-picker-info">
                              <span class="panel-picker-name" [title]="doc.file_name">{{
                                doc.file_name
                              }}</span>
                              <span class="panel-picker-sub">
                                {{ getContextDocFileTypeLabel(doc.file_type) }}
                                · {{ formatFileSize(doc.file_size) }}
                                @if (
                                  doc.uploaded_by_name ?? getDisplayAuthor(doc.uploaded_by);
                                  as uploader
                                ) {
                                  · {{ uploader }}
                                }
                                @if (doc.uploaded_at) {
                                  · {{ formatDate(doc.uploaded_at) }}
                                }
                                @if (extractionDisplay(doc); as extraction) {
                                  ·
                                  <app-status-indicator
                                    appearance="inline"
                                    [status]="extraction.variant"
                                    [label]="extraction.labelKey | transloco"
                                    [icon]="extraction.icon"
                                    [meaning]="extraction.meaning"
                                  />
                                }
                              </span>
                              @if (doc.description) {
                                <span class="panel-picker-sub panel-picker-sub--extra">{{
                                  doc.description
                                }}</span>
                              }
                            </span>
                          </button>
                        </div>
                      }
                    </div>
                  }
                </div>
              } @else {
                <app-empty-state
                  variant="panel"
                  icon="description"
                  [title]="'modals.doc_creation.no_context_docs_yet' | transloco"
                  message=""
                />
              }
            </div>
          </div>
        }
      </div>
    } @else if (currentStep() === 'policy-documents') {
      <!-- Step 4 (DPA only): Linked Policy Documents -->
      <div class="step-body">
        <div class="policy-step">
          <p class="policy-step-intro">
            {{ 'modals.doc_creation.policy_intro' | transloco }}
          </p>

          <div class="policy-step-fields">
            <!-- Cookie Policy -->
            <div class="field">
              <label for="os-cookie-policy">{{
                'modals.doc_creation.field_cookie_policy' | transloco
              }}</label>
              <app-select
                variant="form"
                inputId="os-cookie-policy"
                [placeholder]="'modals.doc_creation.policy_none_placeholder' | transloco"
                [options]="cookiePolicyOptions()"
                [value]="selectedPolicyDocuments().cookiePolicy"
                [disabled]="cookiePolicyOptions().length === 0"
                (valueChange)="onPolicyDocumentSelect('cookiePolicy', $any($event))"
              />
              @if (cookiePolicyDocuments().length === 0) {
                <small>{{ 'modals.doc_creation.no_cookie_policy_docs' | transloco }}</small>
              }
            </div>

            <!-- Privacy Policy -->
            <div class="field">
              <label for="os-privacy-policy">{{
                'modals.doc_creation.field_privacy_policy' | transloco
              }}</label>
              <app-select
                variant="form"
                inputId="os-privacy-policy"
                [placeholder]="'modals.doc_creation.policy_none_placeholder' | transloco"
                [options]="privacyPolicyOptions()"
                [value]="selectedPolicyDocuments().privacyPolicy"
                [disabled]="privacyPolicyOptions().length === 0"
                (valueChange)="onPolicyDocumentSelect('privacyPolicy', $any($event))"
              />
              @if (privacyPolicyDocuments().length === 0) {
                <small>{{ 'modals.doc_creation.no_privacy_policy_docs' | transloco }}</small>
              }
            </div>

            <!-- Terms and Conditions -->
            <div class="field">
              <label for="os-terms">{{ 'modals.doc_creation.field_terms' | transloco }}</label>
              <app-select
                variant="form"
                inputId="os-terms"
                [placeholder]="'modals.doc_creation.policy_none_placeholder' | transloco"
                [options]="termsAndConditionsOptions()"
                [value]="selectedPolicyDocuments().termsAndConditions"
                [disabled]="termsAndConditionsOptions().length === 0"
                (valueChange)="onPolicyDocumentSelect('termsAndConditions', $any($event))"
              />
              @if (termsAndConditionsDocuments().length === 0) {
                <small>{{ 'modals.doc_creation.no_terms_docs' | transloco }}</small>
              }
            </div>

            <!-- Vendor List for Annex B -->
            <div class="field">
              <label for="os-vendor-list">{{
                'modals.doc_creation.field_sub_processor_list' | transloco
              }}</label>
              <app-select
                variant="form"
                inputId="os-vendor-list"
                [placeholder]="'modals.doc_creation.annex_b_none_placeholder' | transloco"
                [options]="vendorListOptions()"
                [value]="selectedVendorListId()"
                [disabled]="vendorListOptions().length === 0"
                (valueChange)="onVendorListSelect($any($event))"
              />
              @if (vendorLists().length === 0) {
                <small>
                  {{ 'modals.doc_creation.no_vendor_lists' | transloco }}
                  <a
                    class="vendor-lists-link"
                    [href]="vendorListsHref()"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ 'modals.doc_creation.no_vendor_lists_link' | transloco }}
                    <mat-icon aria-hidden="true">open_in_new</mat-icon>
                  </a>
                </small>
              } @else if (!selectedVendorListId()) {
                <small>{{ 'modals.doc_creation.no_vendor_list_selected' | transloco }}</small>
              }
            </div>
          </div>
        </div>
      </div>
    } @else if (currentStep() === 'review') {
      <!-- Final step: condensed summary of every selection. Same review-table
         pattern other create wizards use (see wizard-modal.css). -->
      <div class="step-body">
        <!-- The create is the parent's call, so its failure comes back as an
           input. Amber lock when the answer was "you may not", Huxley's
           error view when something actually broke. -->
        @if (createError(); as failure) {
          @if (createErrorIsDenial()) {
            <div role="alert" class="denied-banner">{{ failure }}</div>
          } @else {
            <app-modal-status
              variant="error"
              [title]="'modals.doc_creation.error_title' | transloco"
              [description]="failure"
            >
              <app-huxley [state]="'error'" [size]="72" />
            </app-modal-status>
          }
        }

        <div class="review-table">
          <div class="review-row">
            <span class="review-label">{{
              'modals.doc_creation.review_organization' | transloco
            }}</span>
            <span class="review-value">{{ resolvedOrg()?.name ?? '—' }}</span>
          </div>
          <div class="review-row">
            <span class="review-label">{{
              'modals.doc_creation.review_document_type' | transloco
            }}</span>
            <span class="review-value">{{ selectedDocumentTypeLabel() }}</span>
          </div>
          <div class="review-row">
            <span class="review-label">{{
              'modals.doc_creation.review_document_name' | transloco
            }}</span>
            <span class="review-value">{{ documentName() }}</span>
          </div>
          <div class="review-row">
            <span class="review-label">{{ 'language_picker.label' | transloco }}</span>
            <span class="review-value">
              <app-language-badge [language]="contentLanguage()" appearance="pill" />
            </span>
          </div>
          <div class="review-row">
            <span class="review-label">{{
              'modals.doc_creation.review_context_documents' | transloco
            }}</span>
            <span class="review-value">
              @if (selectedContextDocObjects().length > 0) {
                <span class="review-count">{{
                  'modals.doc_creation.review_count_selected'
                    | transloco: { count: selectedContextDocObjects().length }
                }}</span>
                <span class="review-doc-list">
                  @for (doc of selectedContextDocObjects(); track doc.context_document_id) {
                    <span class="review-chip">{{ doc.file_name }}</span>
                  }
                </span>
              } @else {
                <span class="review-muted">{{
                  'modals.doc_creation.review_none_selected' | transloco
                }}</span>
              }
            </span>
          </div>
          @if (requiresPolicyDocumentsStep()) {
            @let policies = reviewPolicyDocuments();
            <div class="review-row">
              <span class="review-label">{{
                'modals.doc_creation.review_cookie_policy' | transloco
              }}</span>
              <span class="review-value">
                @if (policies.cookiePolicy; as name) {
                  {{ name }}
                } @else {
                  <span class="review-muted">{{
                    'modals.doc_creation.review_not_linked' | transloco
                  }}</span>
                }
              </span>
            </div>
            <div class="review-row">
              <span class="review-label">{{
                'modals.doc_creation.review_privacy_policy' | transloco
              }}</span>
              <span class="review-value">
                @if (policies.privacyPolicy; as name) {
                  {{ name }}
                } @else {
                  <span class="review-muted">{{
                    'modals.doc_creation.review_not_linked' | transloco
                  }}</span>
                }
              </span>
            </div>
            <div class="review-row">
              <span class="review-label">{{ 'modals.doc_creation.review_terms' | transloco }}</span>
              <span class="review-value">
                @if (policies.termsAndConditions; as name) {
                  {{ name }}
                } @else {
                  <span class="review-muted">{{
                    'modals.doc_creation.review_not_linked' | transloco
                  }}</span>
                }
              </span>
            </div>
          }
        </div>
      </div>
    }
  }
</app-modal>

@if (showUploadModal() && resolvedOrg(); as uploadOrg) {
  <app-context-document-upload-modal
    [isVisible]="true"
    [orgId]="uploadOrg.principal_id"
    [orgName]="uploadOrg.name"
    [ownership]="{ usage_scope: 'organization' }"
    (closed)="onUploadModalClosed($event)"
  ></app-context-document-upload-modal>
}
